import {
  Content,
  Page,
  Title,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import {
  StyledFormGroup,
  StyledInput,
  StyledSelect,
  StyledTextArea,
} from '@monorepo/shared/componentsV2/styled/form';
import { Switch } from '@monorepo/shared/componentsV2/Switch';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import {
  isRequired,
  makeIsNoLongerThan,
} from '@monorepo/shared/utils/validators';
import {
  Category,
  getCategoryDisplayString,
  LogRefResponse,
  MAX_COLLECTION_DESCRIPTION_LENGTH,
  UpdateLogRequest,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { useSetLogIsHighFrequency } from '../../hooks/useSetLogIsHighFrequency';
import { EditLogHeader, WizardStep } from './EditLogHeader';
import { Mode } from './types';
import { useSubmitLogDetails } from './useSubmitLogDetails';

const options = Object.values(Category).map((category) => ({
  label: getCategoryDisplayString(category),
  value: category,
}));

export function EditLogPage() {
  useDisableSubnav();

  const { logId, mode = Mode.new, organizationId } = useParams();
  const { log } = useLog({
    logId,
    organizationId,
    config: { useErrorBoundary: true },
  });
  const isEditMode = mode === Mode.edit;
  const logExists = !!logId;

  const onSubmit = useSubmitLogDetails({
    currentWizardStep: WizardStep.DETAILS,
  });
  const isSuperAdmin = useIsSuperAdmin();
  const { confirm } = useModal();
  const { success: showSuccess, showUserFriendlyErrorToast } = useToast();
  const [setIsHighFrequency] = useSetLogIsHighFrequency({
    config: {
      onError: (error) => {
        showUserFriendlyErrorToast(error);
      },
      onSuccess: () => {
        showSuccess('Log set to high frequency.');
      },
    },
  });
  const confirmHighFrequency = useCallback(async () => {
    if (!log || !organizationId) return;
    const isConfirmed = await confirm({
      title: 'High Frequency',
      description: `Are you sure you want to set this log as high frequency? You cannot undo this action.`,
      confirmButtonText: 'Set high frequency',
      cancelButtonText: 'Cancel',
      danger: true,
    });
    if (isConfirmed) {
      await setIsHighFrequency({ logId: log.id, organizationId });
    }
  }, [confirm, setIsHighFrequency, log, organizationId]);

  const initialValues = useMemo(
    () =>
      ({
        ...log,
      } as LogRefResponse),
    [log],
  );

  // This prevents form reinitialization and switch animation issues
  if (isEditMode && !log) {
    return null;
  }

  return (
    <Page>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        subscription={{ pristine: true, submitting: true }}
      >
        {({ handleSubmit, pristine, submitting }) => (
          <>
            <EditLogHeader
              canSubmit={!pristine || (logExists && !isEditMode)}
              isSubmitting={submitting}
              onSubmit={handleSubmit}
              wizardStep={WizardStep.DETAILS}
            />
            <Content>
              <Title>Basic Info</Title>
              <StyledFormGroup row>
                <StyledInput<UpdateLogRequest['name']>
                  label="Log Name"
                  name="name"
                  placeholder="Name"
                  validate={isRequired}
                />
              </StyledFormGroup>
              <StyledFormGroup row>
                <StyledSelect<UpdateLogRequest['category']>
                  label="Compliance Category"
                  name="category"
                  options={options}
                  validate={isRequired}
                />
              </StyledFormGroup>
              <StyledFormGroup row>
                <StyledTextArea<NonNullable<UpdateLogRequest['description']>>
                  label="Description"
                  name="description"
                  placeholder="Type description here"
                  multiline
                  rows={6}
                  validate={makeIsNoLongerThan(
                    MAX_COLLECTION_DESCRIPTION_LENGTH,
                  )}
                />
              </StyledFormGroup>
              {
                // only super admins can set a log as high frequency since the action can't be undone
                isSuperAdmin && log && (
                  <Switch
                    checked={!!log.isHighFrequency}
                    checkedLabel="On"
                    disabled={!!log.isHighFrequency}
                    label="High Frequency"
                    onChange={confirmHighFrequency}
                    uncheckedLabel="Off"
                    helperText="Turn this setting on to set this log as high frequency.
                    It is used to change a log from immediate calculation to scheduled
                    calculation for data that is too processing intensive to calculate immediately.
                    You cannot undo this action."
                  />
                )
              }
            </Content>
          </>
        )}
      </Form>
    </Page>
  );
}
